import { compose, filter, map, negate, omitBy, pick, pipe } from 'lodash/fp';
import * as Utils from 'utils';
import {
  SellOrderDetailedApiAuditor,
  SellOrderDetailedApiOrganization,
  SellOrderDetailedApiOrganizationSigner,
  SellOrderDetailedApiRelation,
  SellOrderDetailedApiResponse,
  ShellCompanyDeclaration,
  ShellCompanyDeclarationCalculations,
} from '../../../utils/sell-process/sell-process-detailed-types';
import { renameProperty } from '../../../utils/rename-property';
import { omitFalsyExceptZeroAndFalse } from '../../../utils/omit-falsy-except-zero-and-false';
import { RelationType } from '../../../global/entities/BuyOrder';
import { objectKeys } from '../../../utils/object-keys';
import { renameSsnToPerson } from '../../../utils/person-entities';
import { renameIt } from '../../../utils/rename-property/rename-property';
import {
  SellOrganizationOwnerForm,
  SellOrganizationSignerStore,
  SellOwnerRelation,
  SellPersonOwnerForm,
} from './components/sell-process-form/components/owners-agreements/owners-agreements-types';
import {
  GetSellRepresentativeRelationsFn,
  SellOrderOrganization,
} from './sell-process-utils-types';
import { SellOrderRepresentativeStore } from './sell-process-types';
import { SellRepresentative } from './components/sell-process-form/components/representatives/representatives-types';

// All references to `pointer` are SSN or Organization Number pointers to entities
// that reside in the `sellOrderPeople` or `sellOrderOrganizations` store slices.

/** Map the form representative values to a relation with `person pointer` */
export const mapSellRepresentativeEntity: (
  x: SellRepresentative
) => SellOrderRepresentativeStore = compose(
  renameSsnToPerson,
  pick(['socialSecurityNumber', 'relationType', 'signer', 'relationId']),
);

/** Map the form person owner values to a relation with `person pointer` */
export const mapSellPersonOwnerEntity = (
  owner: SellOwnerRelation<SellPersonOwnerForm>,
) => ({
  person: owner.socialSecurityNumber,
  city: owner.city,
  relationId: owner.relationId,
  numberShares: owner.numberShares,
  relationType: RelationType.PhysicalShareHolder,
});

export const mapSignerEntity = compose(
  renameSsnToPerson,
  pick(['socialSecurityNumber', 'signerId']),
) as (x: any) => SellOrganizationSignerStore;

/** Map the organization owner values to a relation with `organization pointer` */
export const mapSellOrganizationOwnerEntity = (
  owner: SellOwnerRelation<SellOrganizationOwnerForm>,
) => ({
  organization: owner.organizationNumber,
  signers: map(mapSignerEntity, owner?.signers),
  numberShares: owner.numberShares,
  relationType: RelationType.LegalShareHolder,
  organizationId: owner.organizationId,
});

export function isOrganizationOwner(
  seller: SellOwnerRelation<SellPersonOwnerForm | SellOrganizationOwnerForm>,
): seller is SellOwnerRelation<SellOrganizationOwnerForm> {
  return 'organizationNumber' in seller;
}

export function isPersonOwner(
  seller: SellOwnerRelation<SellPersonOwnerForm | SellOrganizationOwnerForm>,
): seller is SellOwnerRelation<SellPersonOwnerForm> {
  return !isOrganizationOwner(seller);
}

/** Map a seller, either an organization or person, to an entity with either an
 * `organization pointer` or `person pointer` */
export const mapSellerToSellerEntity = (
  seller: SellOwnerRelation<SellPersonOwnerForm | SellOrganizationOwnerForm>,
) => {
  if (isOrganizationOwner(seller)) {
    return mapSellOrganizationOwnerEntity(seller);
  }

  return mapSellPersonOwnerEntity(seller);
};

/** Map over an array and omit all empty strings (useful for omitting ID properties
 * from entities that are being created and thus have no ID) */
export const mapOmitEmptyStrings = map(omitBy((x) => x === ''));

export const pickSellPerson = pick([
  'firstName',
  'lastName',
  'socialSecurityNumber',
  'address',
  'postalCode',
  'city',
  'country',
  'bankId',
  'bankAccount',
]);

export const pickSignerPerson = pick([
  'firstName',
  'lastName',
  'socialSecurityNumber',
]);

export const mapApiRelationToStoreRelation = (
  relation: SellOrderDetailedApiRelation,
) => {
  const formatRelation = (relation: SellOrderDetailedApiRelation) => ({
    ...Utils.pick(relation, [
      'id',
      'personId',
      'relationType',
      'numberShares',
      'bankAccount',
      'bankId',
      'signer',
    ]),
    ...pickSellPerson(relation.person),
  });

  const renameIdToRelationId = renameProperty('id', 'relationId');

  return pipe(
    formatRelation,
    renameIdToRelationId,
    omitFalsyExceptZeroAndFalse,
  )(relation);
};

const isPhysicalShareholder = (relation: SellOrderDetailedApiRelation) =>
  relation.relationType === RelationType.PhysicalShareHolder;

export const getSellerRelations = compose(
  map(mapApiRelationToStoreRelation),
  filter(isPhysicalShareholder),
);

export const getRepresentativeRelations = compose(
  map(mapApiRelationToStoreRelation),
  filter(negate(isPhysicalShareholder)),
) as unknown as GetSellRepresentativeRelationsFn;

export const pickOrganization = pick([
  'id',
  'name',
  'number',
  'address',
  'numberShares',
  'postalCode',
  'city',
  'bankAccount',
  'bankId',
]);

export const getNamesFromFullName = (fullName: string) => ({
  firstName: fullName.slice(0, fullName.lastIndexOf(' ')).trim(),
  lastName: fullName.slice(fullName.lastIndexOf(' ')).trim(),
});

export const getFullName = (person: { firstName: string; lastName: string }) =>
  `${person.firstName} ${person.lastName}`;

export const mapApiOrganizationToStoreOrganization = (
  apiOrganization: SellOrderDetailedApiOrganization,
) => {
  const pickSignerIds = pick(['id', 'personId']);

  const mapApiSignerToStoreSigner = (
    apiSigner: SellOrderDetailedApiOrganizationSigner,
  ) => {
    const signer = {
      ...pickSignerPerson(apiSigner.person),
      ...pickSignerIds(apiSigner),
    };

    return compose(
      renameProperty('id', 'signerId'),
      omitFalsyExceptZeroAndFalse,
    )(signer);
  };

  const organization = {
    ...pickOrganization(apiOrganization),
    signers: map(mapApiSignerToStoreSigner, apiOrganization.signers),
  };

  return compose(
    renameProperty('id', 'organizationId'),
    omitFalsyExceptZeroAndFalse,
  )(organization);
};

export const getSellerOrganizations = (
  organizations: SellOrderDetailedApiOrganization[],
) =>
  map(
    mapApiOrganizationToStoreOrganization,
    organizations,
  ) as SellOrderOrganization[];

export function pickShellCompanyDeclaration(
  params: ShellCompanyDeclaration & ShellCompanyDeclarationCalculations,
) {
  return pick(
    [
      'id',
      'sharesInGroupCompaniesAssets',
      'receivablesFromOtherCompaniesAssets',
      'cashBankAccountingFundsAssets',
      'restrictedEquityLiabilities',
      'unrestrictedEquityLiabilities',
      'supplierLiabilitiesLiabilities',
      'liabilitiesToGroupLiabilities',
      'liabilitiesToOtherCompaniesLiabilities',
      'taxLiabilitiesLiabilities',
      'accruedExpensesLiabilities',
      'netSalesIncomeStatement',
      'changeOfStockIncomeStatementPositive',
      'changeOfStockIncomeStatementNegative',
      'otherOperatingIncomeIncomeStatement',
      'rawMaterialsIncomeStatement',
      'otherExternalCostsIncomeStatement',
      'personnelCostsIncomeStatement',
      'depreciationOfFixedAssetsIncomeStatement',
      'otherOperatingCostsIncomeStatement',
      'resultsFromSharesIncomeStatementPositive',
      'resultsFromSharesIncomeStatementNegative',
      'resultsFromFixedAssetsIncomeStatementPositive',
      'resultsFromFixedAssetsIncomeStatementNegative',
      'otherInterestIncomeIncomeStatement',
      'interestCostsIncomeStatement',
      'reversalOfAccrualFundIncomeStatement',
      'reversalOfExcessDepreciationStatementPositive',
      'reversalOfExcessDepreciationStatementNegative',
      'taxOnYearsProfitIncomeStatement',
      'profitIncomeStatement',
      'lossIncomeStatement',
      'profitTaxAdjustments',
      'lossTaxAdjustments',
      'taxOnYearsProfitTaxAdjustments',
      'impairmentOfAssetsTaxAdjustments',
      'otherRecordedCostsTaxAdjustments',
      'pieceProfitsTaxAdjustments',
      'dividendTaxAdjustments',
      'otherRecordedIncomeTaxAdjustments',
      'revaluedReversalAccrualFundTaxAdjustments',
      'coOwnershipDisposalRecordedProfit',
      'coOwnershipDisposalRecordedLoss',
      'unusedDeficitPrevYearTaxAdjustments',
      'reductionOfUnusedDeficitTaxAdjustments',
      'surplusTaxAdjustments',
      'deficitTaxAdjustments',
    ],
    params,
  );
}

export function pickSellAuditorCompany(auditor: SellOrderDetailedApiAuditor) {
  return pick(
    [
      'organizationName',
      'organizationNumber',
      'organizationAddress',
      'organizationPostalCode',
      'organizationCity',
      'organizationSigner',
    ],
    auditor,
  );
}

export function pickSellMetadata(sellOrder: SellOrderDetailedApiResponse) {
  return renameIt(
    Utils.pick(sellOrder, [
      'id',
      'dateOffer',
      'dateAgreement',
      'status',
      'shareCapital',
      'numberShares',
      'purchasePrice',
      'contractWording',
      'equity',
      'internalBank',
      'acquiredCash',
      'externalBank',
      'companyBank',
      'companyBankAccount',
      'tcNumber',
      'commision',
      'commisionAmount',
      'settlementType',
      'anotherBuyer',
      'fiscalYear',
      'fiscalStartDate',
      'fiscalEndDate',
      'datePurchased',
      'dateDecided',
      'dateSent',
      'dateExtraMeetingProtocol',
      'auditorReservation',
      'childOrdersDetails',
      'hasChildOrder',
      'parentOrderId',
      'AGAregistered',
      'VATregistered',
      'AGAcreatedFrom',
      'VATcreatedFrom',
      'dateOfRevocationFTax',
      'shellCompanyDeclaration',
    ]),
    'settlementType',
    'settelmentType',
  );
}

export const getStaffRelations = (sellOrder: SellOrderDetailedApiResponse) => {
  type MappedStaffRelation = {
    id: string;
    userId: string;
  };

  const staffRelations = Utils.pick(sellOrder, [
    'liquidator',
    'adjuster',
    'signer',
    'businessManager',
    'taxOfficeContactPerson',
  ]);

  return objectKeys(staffRelations).reduce(
    (acc, relation) => ({
      ...acc,
      [relation]: {
        id: sellOrder[relation].id,
        userId: sellOrder[relation].user.id,
      },
    }),
    {} as Record<keyof typeof staffRelations, MappedStaffRelation>,
  );
};
